import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'fr' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    fr: 'Équipe',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    fr: 'Matchs',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    fr: 'Offres',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    fr: 'Résultats en direct',
    es: 'Resultados en vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    fr: 'Commandez le vôtre',
    es: 'Pide el tuyo'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of France 2026",
    fr: 'Vous faites partie de France 2026',
    es: 'Ahora eres parte de Francia 2026'
  },
  'hero.title1': {
    en: "Don't Just Support France,",
    fr: 'Ne te contente pas de supporter la France,',
    es: 'No solo apoyes a Francia,'
  },
  'hero.title2': {
    en: 'Live It',
    fr: 'Vis-le',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    fr: 'Mis à jour en temps réel. Accédez à du contenu exclusif, des mises à jour de matchs et des offres de voyage directement depuis votre hub numérique.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    fr: 'Voir les matchs',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    fr: "Explorer l'équipe",
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    fr: 'Faites défiler pour découvrir',
    es: 'Desplázate para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    fr: 'Notre Héritage',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    fr: 'Une Nation Forgée',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    fr: 'Dans la Gloire',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: 'The French national team represents more than just football; it embodies the passion, resilience, and artistry of a nation. Founded in 1904, Les Bleus have conquered the world twice and Europe twice, continuing to write their name in the stars.',
    fr: "L'équipe nationale française représente plus que du football ; elle incarne la passion, la résilience et le talent d'une nation. Fondés en 1904, Les Bleus ont conquis le monde deux fois et l'Europe deux fois, continuant d'écrire leur nom dans les étoiles.",
    es: 'La selección francesa representa más que solo fútbol; encarna la pasión, la resiliencia y el arte de una nación. Fundados en 1904, Les Bleus han conquistado el mundo dos veces y Europa dos veces, continuando escribiendo su nombre en las estrellas.'
  },
  'team.since': {
    en: 'Since 1904',
    fr: 'Depuis 1904',
    es: 'Desde 1904'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    fr: 'Explorez notre histoire complète →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: 'Euro 1984 & 2000',
    fr: 'Euro 1984 & 2000',
    es: 'Euro 1984 & 2000'
  },
  'team.euroDesc': {
    en: 'Champions of Europe',
    fr: "Champions d'Europe",
    es: 'Campeones de Europa'
  },
  'team.nationsLeague': {
    en: 'Nations League',
    fr: 'Ligue des Nations',
    es: 'Liga de Naciones'
  },
  'team.nationsLeagueDesc': {
    en: '2021 Winners',
    fr: 'Vainqueurs 2021',
    es: 'Ganadores 2021'
  },
  'team.euros': {
    en: '11 Euros',
    fr: '11 Euros',
    es: '11 Euros'
  },
  'team.eurosDesc': {
    en: 'Championship Appearances',
    fr: 'Participations au Championnat',
    es: 'Participaciones en Campeonatos'
  },
  'team.worldCups': {
    en: '2 World Cups',
    fr: '2 Coupes du Monde',
    es: '2 Copas del Mundo'
  },
  'team.worldCupsDesc': {
    en: 'Champions 1998 & 2018',
    fr: 'Champions 1998 & 2018',
    es: 'Campeones 1998 & 2018'
  },
  'team.videoLabel': {
    en: 'Allez Les Bleus',
    fr: 'Allez Les Bleus',
    es: 'Allez Les Bleus'
  },
  'team.videoTitle1': {
    en: 'One Nation,',
    fr: 'Une Nation,',
    es: 'Una Nación,'
  },
  'team.videoTitle2': {
    en: 'Allez Les Bleus',
    fr: 'Allez Les Bleus',
    es: 'Allez Les Bleus'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    fr: "L'Équipe",
    es: 'La Plantilla'
  },
  'players.title1': {
    en: 'The Stars of',
    fr: 'Les Étoiles de',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'France',
    fr: 'France',
    es: 'Francia'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    fr: "Voir l'équipe complète sur ESPN.com",
    es: 'Ver Plantilla Completa en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    fr: 'Attaquant',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    fr: 'Milieu de terrain',
    es: 'Centrocampista'
  },
  'players.defender': {
    en: 'Defender',
    fr: 'Défenseur',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    fr: 'Coupe du Monde 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    fr: 'Centre de',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    fr: 'Matchs',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow Les Bleus in Group E. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    fr: 'Suivez Les Bleus dans le Groupe E. Obtenez des mises à jour en direct, des compositions officielles et des scores en temps réel de la plus grande scène du football.',
    es: 'Sigue a Les Bleus en el Grupo E. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    fr: 'Classement du Groupe',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    fr: 'En Direct',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    fr: 'Actualisation auto toutes les 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    fr: 'Actualiser',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    fr: 'Équipe',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    fr: 'Voir le classement en direct sur FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    fr: 'Prochain Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    fr: 'Calendrier ESPN',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    fr: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    fr: 'FlashScore en direct',
    es: 'FlashScore en vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    fr: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'France By The Numbers',
    fr: 'La France en Chiffres',
    es: 'Francia en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    fr: 'Participations en Coupe du Monde',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    fr: 'Titres Majeurs',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    fr: 'Buts Internationaux',
    es: 'Goles Internacionales'
  },
  'stats.ronaldo': {
    en: 'Giroud Goals (Record)',
    fr: 'Buts de Giroud (Record)',
    es: 'Goles de Giroud (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    fr: 'Les Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    fr: 'Des Moments Qui',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    fr: "Ont Fait l'Histoire",
    es: 'Hicieron Historia'
  },
  'history.euro2016': {
    en: '1998 World Cup Final',
    fr: 'Finale de la Coupe du Monde 1998',
    es: 'Final del Mundial 1998'
  },
  'history.eder': {
    en: '2018 World Cup Final',
    fr: 'Finale de la Coupe du Monde 2018',
    es: 'Final del Mundial 2018'
  },
  'history.ronaldoGoals': {
    en: "France's Most Memorable World Cup Goals",
    fr: 'Les Buts les Plus Mémorables en Coupe du Monde',
    es: 'Los Goles Más Memorables de Francia en el Mundial'
  },
  'history.nationsLeague': {
    en: 'Euro 2000 Golden Goal',
    fr: 'But en Or Euro 2000',
    es: 'Gol de Oro Euro 2000'
  },
  'history.platini': {
    en: 'Platini Euro 1984',
    fr: 'Platini Euro 1984',
    es: 'Platini Euro 1984'
  },
  'history.mbappe': {
    en: '2 Goals in 95 Seconds! Mbappé 2022 Final',
    fr: '2 Buts en 95 Secondes ! Mbappé Finale 2022',
    es: '2 Goles en 95 Segundos! Mbappé Final 2022'
  },
  'history.henry': {
    en: '2018 FIFA World Cup | Every France Goal',
    fr: 'Coupe du Monde 2018 | Tous les Buts de la France',
    es: 'Mundial 2018 | Todos los Goles de Francia'
  },
  'history.zidaneBrazil': {
    en: 'Zidane vs Brazil 2006',
    fr: 'Zidane vs Brésil 2006',
    es: 'Zidane vs Brasil 2006'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    fr: "Plus Qu'un Jeu",
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    fr: 'Ressentez la Passion de la',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'France',
    fr: 'France',
    es: 'Francia'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    fr: "Rejoignez des millions de voix chantant à l'unisson. Portez les couleurs, ressentez l'histoire et participez au voyage vers la gloire mondiale.",
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    fr: 'Voyagez pour la Coupe du Monde',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    fr: 'Offres',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    fr: 'Exclusives',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for France fans on tickets, hotels, flights & transport to support Les Bleus in North America.',
    fr: 'Des remises exclusives pour les fans de la France sur les billets, les hôtels, les vols et les transports pour soutenir Les Bleus en Amérique du Nord.',
    es: 'Descuentos exclusivos para fans de Francia en entradas, hoteles, vuelos y transporte para apoyar a Les Bleus en América del Norte.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    fr: 'Débloquez des remises VIP',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    fr: 'Appliquez le code lors du paiement sur les sites partenaires',
    es: 'Aplica el código en el checkout de los sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    fr: 'Billets de Match',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    fr: "Jusqu'à 15% de réduction sur les forfaits de phase de groupes",
    es: 'Hasta 15% de descuento en paquetes de la fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    fr: 'Trouver des Billets',
    es: 'Encontrar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    fr: 'Hôtels & Hébergement',
    es: 'Hoteles & Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    fr: "Offres d'hôtels partenaires près des stades",
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    fr: 'Réserver des Hôtels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    fr: 'Vols',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Paris & Lyon to USA',
    fr: 'Tarifs spéciaux de Paris & Lyon vers les USA',
    es: 'Tarifas especiales de París y Lyon a EE. UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    fr: 'Rechercher des Vols',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    fr: 'Transport Local',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    fr: 'Services de navette et location de voitures dans les villes hôtes',
    es: 'Servicios de transporte y alquiler de coches en las ciudades de los partidos'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    fr: 'Planifier le Transport',
    es: 'Planificar Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for France 2026',
    fr: 'Tout ce dont vous avez besoin pour France 2026',
    es: 'Todo lo que necesitas para Francia 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    fr: 'QG des',
    es: 'Sede del'
  },
  'fanHq.title2': {
    en: 'HQ',
    fr: 'Fans',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to France's First Match",
    fr: 'Compte à rebours avant le premier match de la France',
    es: 'Cuenta regresiva para el primer partido de Francia'
  },
  'fanHq.days': {
    en: 'Days',
    fr: 'Jours',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    fr: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    fr: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    fr: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    fr: 'Capacité',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    fr: 'Apprenez les',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    fr: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    fr: 'Hymne National',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    fr: 'Chant Classique de Stade',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    fr: 'Favori de la Foule',
    es: 'Favorito del Público'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    fr: 'Chant de Victoire',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    fr: 'Voir sur YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    fr: 'Phrases',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    fr: 'Essentielles',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    fr: 'Liens',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    fr: 'Rapides',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    fr: 'Où Regarder',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    fr: 'Chaînes de streaming et TV',
    es: 'Canales de streaming y TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    fr: 'Canal Info Fans',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    fr: 'Informations officielles FIFA pour les fans',
    es: 'Información oficial de la FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    fr: 'Classement FIFA',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    fr: 'Classements mondiaux officiels',
    es: 'Clasificaciones mundiales oficiales'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    fr: 'Calendrier Complet de la CM',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    fr: 'Les 104 matchs',
    es: 'Los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for France 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    fr: 'Un produit numérique officiel Qtap par STDI. Votre hub de T-shirt NFC intelligent pour France 2026 — données de match en temps réel, contenu exclusif et offres de voyage à portée de main.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para Francia 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    fr: 'Ressources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    fr: 'Équipe',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    fr: 'Équipe Première',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    fr: 'Matchs',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    fr: 'Classements',
    es: 'Clasificaciones'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    fr: 'Offres de Voyage',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    fr: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    fr: 'Nous Contacter',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    fr: 'FAQ',
    es: 'FAQ'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    fr: 'Politique de Confidentialité',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    fr: 'Conditions de Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    fr: 'Politique de Cookies',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    fr: 'Qtap par STDI. Tous droits réservés.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => {
      if (prev === 'en') return 'fr';
      if (prev === 'fr') return 'es';
      return 'en';
    });
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}